import { updateMonitorRoomImage } from '@/api/jiankao'
import {
  type OssUploadContext,
  type OssUploadResult,
  type OssUploadedContext,
  type UseOssUploadOptions,
  useOssUpload
} from './useOssUpload'

export type MonitorRoomImagePosition = 'master' | 'desk' | 'slave'

export interface MonitorRoomImageUploadPayload {
  actiLinkId: string | number
  roomGroupId: string | number
  position: MonitorRoomImagePosition
}

export interface UseMonitorRoomImageUploadOptions
  extends Partial<UseOssUploadOptions<MonitorRoomImageUploadPayload>> {
  disableUpdateImage?: boolean
}

function formatDatePathPart(timestamp: number) {
  const date = new Date(timestamp)
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}${month}${day}`
}

function buildDefaultMonitorRoomObjectName({
  payload,
  accountId,
  timestamp,
  randomSuffix,
  ext
}: OssUploadContext<MonitorRoomImageUploadPayload>) {
  const datePart = formatDatePathPart(timestamp)
  return `monitor-room/${payload.roomGroupId}/${payload.actiLinkId}/${accountId}/${payload.position}/${datePart}/${timestamp}_${randomSuffix}${ext}`
}

async function saveMonitorRoomImage({
  payload,
  accountId,
  url
}: OssUploadedContext<MonitorRoomImageUploadPayload>) {
  await updateMonitorRoomImage({
    actiLinkId: payload.actiLinkId,
    accountId: accountId as number,
    roomGroupId: payload.roomGroupId,
    imageUrl: url,
    position: payload.position
  })
}

export function useMonitorRoomImageUpload(options?: UseMonitorRoomImageUploadOptions) {
  const uploadOptions: UseOssUploadOptions<MonitorRoomImageUploadPayload> = {
    buildObjectName: (ctx) => {
      return options?.buildObjectName?.(ctx) || buildDefaultMonitorRoomObjectName(ctx)
    },
    resolveUrl: (ctx) => {
      return options?.resolveUrl?.(ctx) || ctx.url
    },
    onUploaded: async (ctx) => {
      if (options?.onUploaded) {
        await options.onUploaded(ctx)
        return
      }

      if (options?.disableUpdateImage) {
        return
      }

      await saveMonitorRoomImage(ctx)
    }
  }

  const { uploadToOss, uploadMonitorImageToOss, getStsToken, isTokenValid } =
    useOssUpload<MonitorRoomImageUploadPayload>(uploadOptions)

  const uploadRoomImageToOss = async (
    file: File | Blob,
    payload: MonitorRoomImageUploadPayload
  ): Promise<OssUploadResult> => {
    return uploadToOss(file, payload)
  }

  return {
    uploadToOss: uploadRoomImageToOss,
    uploadMonitorImageToOss,
    getStsToken,
    isTokenValid
  }
}
